
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action } from "vuex-class"

import moment from "moment"

import api from "@/services/api"
import eventApi from "@/services/api.event"

import { Media, Entry, CaseDetails } from "@/types"
import { Event, CaseEvent, CaseEvents, EventMedia, EventRecord } from "@/types/originatingEvents"
import { KeyMedia } from "@/types/originatingEvents/keyMedia"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import SkeletonLoader from "@/components/SkeletonLoader.vue"

const caseStore = namespace("cases")

@Component({
	components: {
		"skeleton": SkeletonLoader,
		"scrollbar": VuePerfectScrollbar
	}
})
export default class CCEvent extends Vue {
	@Prop()
	public event!: CaseEvent;

	@Prop({ type: String, default: "" })
	public fieldId!: string;

	@Prop({ type: String, default: "" })
	public value!: string;

	@Prop({ type: Boolean, default: true })
	public showTitle!: boolean;

	@Prop()
	public currentCase!: CaseDetails;

	@Prop()
	canEditCurrentCase!: boolean;

	@Prop({ default: () => null })
	public caseEvents!: CaseEvents | null;

	@caseStore.Mutation
	public setPreviewKeyMedia!: (mediaClip: EventMedia) => void;

	public statusUpdateTimer: NodeJS.Timeout | null = null;

	eventDetails: Event | null = null;
	eventMedia: EventMedia[] = [];

	private get keyMedia () : Set<string> {
		if (!this.caseEvents) return new Set<string>()
		const event = this.caseEvents.originatingEvents.find(e => e.id === this.eventDetails!.id)
		if (!event || !event.keyMedia) return new Set<string>()

		return new Set<string>(event.keyMedia.map(m => m.id))
	}

	private get keyEventRecords () : Set<number> {
		if (!this.caseEvents) return new Set<number>()
		const event = this.caseEvents.originatingEvents.find(e => e.id === this.eventDetails!.id)
		if (!event || !event.keyEntries) return new Set<number>()

		return new Set<number>(event.keyEntries.map(m => m.eventRecordId))
	}

	public async mounted () {
		if (this.event) {
			this.eventDetails = await eventApi.eventGet(this.currentCase.id!, this.event.id)

			// do not await!
			eventApi.eventMediaGet(this.currentCase.id!, this.event.id).then(mediaList => {
				this.eventMedia = mediaList

				mediaList.forEach(m => {
					if (this.eventDetails && m.eventRecordId > 0) {
						var eventRecord = this.eventDetails.eventRecords.find(r => r.eventRecordId === m.eventRecordId)
						if (eventRecord) {
							if (eventRecord.clips) {
								eventRecord.clips.push(m)
							} else {
								Vue.set(eventRecord, "clips", [m])
							}
						}
					}
				})
			})
		}
	}

	@Watch("value")
	public async onValueChanged (newValue: any) {
		// Load event
	}

	private polarToCartesian (centerX: number, centerY: number, radius: number, angleInDegrees: number) {
		const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0

		return {
			x: centerX + (radius * Math.cos(angleInRadians)),
			y: centerY + (radius * Math.sin(angleInRadians))
		}
	}

	public arcPath (clip: any): string {
		const startAngle = 0
		const endAngle = 360 * (clip.uploadPercent / 100)
		const radius = 10

		const start = this.polarToCartesian(15, 15, radius, endAngle)
		const end = this.polarToCartesian(15, 15, radius, startAngle)

		var largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1"

		var d = [
			"M", start.x, start.y,
			"A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
			"L", 15, 15
		].join(" ")

		return d
	}

	public addIfUnadded (clip: EventMedia): void {
		if (!this.keyMedia.has(clip.id)) {
			eventApi.mediaMarkKey(this.currentCase!.id!, clip.id, true)
		}
	}

	public async addEntry (eventRecord: EventRecord) {
		if (!this.keyEventRecords.has(eventRecord.eventRecordId)) {
			Vue.set(eventRecord, "pending", true)

			try {
				await eventApi.eventRecordMarkKey(this.currentCase!.id!, this.event.id, eventRecord.eventRecordId, true)
			} finally {
				Vue.set(eventRecord, "pending", false)
			}
		}
	}

	public asLocal (dateString: string) {
		return dateString ? moment(dateString).format("l LTS") : ""
	}

	private thumbnailPath (mediaClip: EventMedia): string {
		return api.baseUrl + `event/download/${this.currentCase.id}/${mediaClip.id}?thumbnail=true`
	}

	private isKey (eventRecord: EventRecord): boolean {
		return this.keyEventRecords.has(eventRecord.eventRecordId)
	}

	private isKeyMedia (mediaClip: EventMedia): boolean {
		return this.keyMedia.has(mediaClip.id)
	}
}
